import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { startCase } from 'es-toolkit';

import { dashboardTabs, groupedDashboardTabs } from '/business/shared/util/Statics';

import getDashboardTabsFromTabGroup from '/business/shared/functions/getDashboardTabsFromTabGroup';

import setDashboardApplicationsState from '/business/shared/state/actions/applications/setDashboardApplicationsState';
import getApplicationPackagesSelector from '/business/shared/state/selectors/applications/getApplicationPackagesSelector';
import getDashboardApplicationsStateSelector from '/business/shared/state/selectors/applications/getDashboardApplicationsStateSelector';
import getAnnualReviewsApplicationsSelector from '/business/shared/state/selectors/applications/getAnnualReviewsApplicationsSelector';

import StyledHeaderTabs from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabs';
import StyledHeaderTabsListItem from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabsListItem';
import StyledHeaderTabsList from '/business/shared/components/widgets/header/__styled__/StyledHeaderTabsList';
import type { AppDispatch } from '/business/shared/state/store';
import type { HeaderTabItem } from '../types';

type DashboardTab = (typeof dashboardTabs)[keyof typeof dashboardTabs];

function HeaderDashboardTabs() {
  const dispatch = useDispatch<AppDispatch>();

  const applicationPackages = useSelector(getApplicationPackagesSelector);
  const activeTab = useSelector(getDashboardApplicationsStateSelector) || dashboardTabs.VIEW_ALL;
  const annualReviewsApplications = useSelector(getAnnualReviewsApplicationsSelector);

  const { t } = useTranslation();

  // Build a set of package IDs that are in the annual updates list
  // so we can exclude them from their normal state group and count them under SUBMITTED instead
  const annualUpdatePackageIds = useMemo(() => {
    return new Set(annualReviewsApplications.map((app) => app.packageId));
  }, [annualReviewsApplications]);

  const activeApplicationPackages = applicationPackages?.filter((pkg) => !pkg.archived);

  const dashboardTabsCountV3: Record<string, number> = {};
  Object.values(dashboardTabs).forEach((tab: DashboardTab) => {
    // Non-archived packages for state tabs (to avoid double-counting with Complete tab),
    // all packages for VIEW_ALL (shows both active + archived) and ARCHIVED (filters to archived only)
    const packagesToCount =
      tab === dashboardTabs.VIEW_ALL || tab === dashboardTabs.ARCHIVED
        ? applicationPackages
        : activeApplicationPackages;

    dashboardTabsCountV3[tab] = packagesToCount?.filter((applicationPackage) => {
      if (tab === dashboardTabs.VIEW_ALL) {
        return true;
      }

      const isAnnualUpdate = annualUpdatePackageIds.has(applicationPackage.id);

      // Annual update packages should only be counted under SUBMITTED (Awaiting Action),
      // not under their normal state group
      if (isAnnualUpdate) {
        return tab === dashboardTabs.SUBMITTED;
      }

      const stateItems = getDashboardTabsFromTabGroup(tab).map((stateRule) => {
        return stateRule.state;
      });

      const groupedState =
        Object.keys(groupedDashboardTabs).find((groupedKey) =>
          groupedDashboardTabs[groupedKey].includes(applicationPackage.applicationState),
        ) || applicationPackage.applicationState;
      if (stateItems.includes(groupedState)) {
        return true;
      }
      return false;
    }).length;
  });

  const archivedCount = applicationPackages?.filter((pkg) => pkg.archived).length || 0;

  const items: HeaderTabItem[] = [
    {
      key: dashboardTabs.VIEW_ALL,
      title: t('business:ApplicationStates.viewAll', startCase(dashboardTabs.VIEW_ALL)),
      href: '',
      state: dashboardTabs.VIEW_ALL,
      testId: 'dashboard-viewall',
      isActive: activeTab === dashboardTabs.VIEW_ALL,
      count: dashboardTabsCountV3[dashboardTabs.VIEW_ALL],
      isVisible: true,
    },
    {
      key: dashboardTabs.DRAFT,
      title: t('business:ApplicationStates.draft', startCase(dashboardTabs.DRAFT)),
      href: '',
      state: dashboardTabs.DRAFT,
      testId: 'dashboard-draft',
      isActive: activeTab === dashboardTabs.DRAFT,
      count: dashboardTabsCountV3[dashboardTabs.DRAFT],
      isVisible: dashboardTabsCountV3[dashboardTabs.DRAFT] > 0,
    },
    {
      key: dashboardTabs.COMPLETED,
      title: t('business:ApplicationStates.completed', startCase(dashboardTabs.COMPLETED)),
      href: '',
      state: dashboardTabs.COMPLETED,
      testId: 'dashboard-completed',
      isActive: activeTab === dashboardTabs.COMPLETED,
      count: dashboardTabsCountV3[dashboardTabs.COMPLETED],
      isVisible: dashboardTabsCountV3[dashboardTabs.COMPLETED] > 0,
    },
    {
      key: dashboardTabs.SIGNED,
      title: t('business:ApplicationStates.signed', startCase(dashboardTabs.SIGNED)),
      href: '',
      state: dashboardTabs.SIGNED,
      testId: 'dashboard-signed',
      isActive: activeTab === dashboardTabs.SIGNED,
      count: dashboardTabsCountV3[dashboardTabs.SIGNED],
      isVisible: dashboardTabsCountV3[dashboardTabs.SIGNED] > 0,
    },
    {
      key: dashboardTabs.SUBMITTED,
      title: t('business:ApplicationStates.submitted', startCase(dashboardTabs.SUBMITTED)),
      href: '',
      state: dashboardTabs.SUBMITTED,
      testId: 'dashboard-submitted',
      isActive: activeTab === dashboardTabs.SUBMITTED,
      count: dashboardTabsCountV3[dashboardTabs.SUBMITTED],
      isVisible: dashboardTabsCountV3[dashboardTabs.SUBMITTED] > 0,
    },
    {
      key: dashboardTabs.APPROVED,
      title: t('business:ApplicationStates.approved', startCase(dashboardTabs.APPROVED)),
      href: '',
      state: dashboardTabs.APPROVED,
      testId: 'dashboard-approved',
      isActive: activeTab === dashboardTabs.APPROVED,
      count: dashboardTabsCountV3[dashboardTabs.APPROVED],
      isVisible: dashboardTabsCountV3[dashboardTabs.APPROVED] > 0,
    },
    {
      key: dashboardTabs.CONFIRMED,
      title: t('business:ApplicationStates.confirmed', startCase(dashboardTabs.CONFIRMED)),
      href: '',
      state: dashboardTabs.CONFIRMED,
      testId: 'dashboard-confirmed',
      isActive: activeTab === dashboardTabs.CONFIRMED,
      count: dashboardTabsCountV3[dashboardTabs.CONFIRMED],
      isVisible: dashboardTabsCountV3[dashboardTabs.CONFIRMED] > 0,
    },
    {
      key: dashboardTabs.ARCHIVED,
      title: t('business:ApplicationStates.archived', 'Complete'),
      href: '',
      state: dashboardTabs.ARCHIVED,
      testId: 'dashboard-complete',
      isActive: activeTab === dashboardTabs.ARCHIVED,
      count: archivedCount,
      isVisible: archivedCount > 0,
    },
  ];

  const countApplicationsInActiveTab = useMemo(() => {
    if (activeTab === dashboardTabs.ARCHIVED) {
      return archivedCount;
    }
    return activeApplicationPackages?.filter((application) => {
      if (activeTab === dashboardTabs.VIEW_ALL) {
        return true;
      }
      const isAnnualUpdate = annualUpdatePackageIds.has(application.id);
      if (isAnnualUpdate) {
        return activeTab === dashboardTabs.SUBMITTED;
      }
      const groupedState =
        Object.keys(groupedDashboardTabs).find((key) =>
          groupedDashboardTabs[key].includes(application.applicationState),
        ) || application.applicationState;
      if (groupedState === activeTab) {
        return true;
      }
      return false;
    }).length;
  }, [activeApplicationPackages, activeTab, archivedCount, annualUpdatePackageIds]);

  if (countApplicationsInActiveTab === 0 && activeTab !== dashboardTabs.VIEW_ALL) {
    dispatch(setDashboardApplicationsState(dashboardTabs.VIEW_ALL));
  }

  return (
    <StyledHeaderTabs>
      <StyledHeaderTabsList>
        {items.map((item) => (
          <StyledHeaderTabsListItem
            key={item.key}
            $isDashboardListItemStyle
            data-test-id={item.testId}
            $isActive={item.isActive}
            $isVisible={item.isVisible}
            hasThinPadding={(item.count ?? 0) > 99}
            onClick={() => {
              if (item.state) {
                dispatch(setDashboardApplicationsState(item.state));
              }
            }}
          >
            <div>
              <div>{item.title}</div>
              <div>
                <div>{item.count}</div>
              </div>
            </div>
          </StyledHeaderTabsListItem>
        ))}
      </StyledHeaderTabsList>
    </StyledHeaderTabs>
  );
}

export default HeaderDashboardTabs;
